import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  FiCheck,
  FiChevronLeft,
  FiChevronRight,
  FiHeart,
  FiMinus,
  FiPlus,
  FiX,
} from "react-icons/fi";
import productData from "../Datas/productData";
import siteConfig, { formatPrice } from "../config/siteConfig";
import { CartContext } from "./CartContext";
import "../css/productdetails.scss";

const colorValues = {
  Black: "#171717",
  White: "#f7f5ef",
  Ecru: "#e8dfcf",
  Stone: "#b8afa4",
  "Dark Fog": "#5d5a58",
  "Light Fudge": "#b9a18c",
  "Powder Pink": "#d9aaa9",
  Moonrock: "#908a82",
  "Light Denim": "#aebdca",
  "Dark Denim": "#40556a",
  "Steel Blue": "#647584",
  "Dusty Olive": "#777866",
  "Dark Grey": "#353535",
  "Smoked Amber": "#766a5f",
  "Amber Glass": "#6d5441",
  "Ecru / Black": "#d8d0c2",
  Oatmeal: "#d8cdbb",
  Charcoal: "#424242",
  "Ink Blue": "#27384b",
  "Warm Sand": "#cbb89d",
  Clay: "#a9674f",
  "Dusty Rose": "#b78e91",
  "Dark Brown": "#4b3327",
  "Warm Stone": "#aaa093",
  Cocoa: "#715346",
};

const SIZE_GUIDE = [
  { size: "XXS", bust: "78–82", waist: "60–64", hip: "86–90" },
  { size: "XS", bust: "82–86", waist: "64–68", hip: "90–94" },
  { size: "S", bust: "86–90", waist: "68–72", hip: "94–98" },
  { size: "M", bust: "90–96", waist: "72–78", hip: "98–104" },
  { size: "L", bust: "96–102", waist: "78–84", hip: "104–110" },
  { size: "XL", bust: "102–108", waist: "84–90", hip: "110–116" },
  { size: "2XL", bust: "108–116", waist: "90–98", hip: "116–124" },
];

function ProductImage({ src, alt, className = "" }) {
  const [status, setStatus] = useState("loading");

  return (
    <div className={`product-image-shell ${className} is-${status}`}>
      {status === "loading" && <span className="product-image-loader" />}
      {status === "error" ? (
        <span className="product-image-error">Image unavailable</span>
      ) : (
        <img
          src={src}
          alt={alt}
          onLoad={() => setStatus("ready")}
          onError={() => setStatus("error")}
        />
      )}
    </div>
  );
}

const ProductDetail = () => {
  const { id } = useParams();
  const product = productData.find((item) => item.id === Number(id));
  const { addToCart, toggleWishlist, isWishlisted, openCart } =
    useContext(CartContext);
  const [selectedSize, setSelectedSize] = useState("");
  const [selectedColor, setSelectedColor] = useState("");
  const [selectedImage, setSelectedImage] = useState(0);
  const [quantity, setQuantity] = useState(1);
  const [optionError, setOptionError] = useState("");
  const [added, setAdded] = useState(false);
  const [sizeGuideOpen, setSizeGuideOpen] = useState(false);
  const mobileGalleryRef = useRef(null);
  const sizeGuideTriggerRef = useRef(null);
  const sizeGuideCloseRef = useRef(null);

  useEffect(() => {
    setSelectedSize("");
    setSelectedColor(product?.colors?.[0] || "");
    setSelectedImage(0);
    setQuantity(1);
    setOptionError("");
    setAdded(false);
    setSizeGuideOpen(false);
    document.title = product
      ? `${product.name} | ${siteConfig.brandName}`
      : `Product not found | ${siteConfig.brandName}`;
  }, [product]);

  useEffect(() => {
    if (!sizeGuideOpen) return undefined;

    const previousOverflow = document.body.style.overflow;
    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        setSizeGuideOpen(false);
        window.requestAnimationFrame(() => sizeGuideTriggerRef.current?.focus());
      }

      if (event.key === "Tab") {
        event.preventDefault();
        sizeGuideCloseRef.current?.focus();
      }
    };

    document.body.style.overflow = "hidden";
    document.addEventListener("keydown", handleKeyDown);
    sizeGuideCloseRef.current?.focus();

    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [sizeGuideOpen]);

  const galleryImages = product?.images?.length
    ? product.images
    : product?.image
      ? [product.image]
      : [];

  const relatedProducts = useMemo(() => {
    if (!product) return [];
    const sameCategory = productData.filter(
      (item) => item.id !== product.id && item.category === product.category
    );
    const remaining = productData.filter(
      (item) =>
        item.id !== product.id &&
        !sameCategory.some((related) => related.id === item.id)
    );
    return [...sameCategory, ...remaining].slice(0, 4);
  }, [product]);

  if (!product) {
    return (
      <main className="product-detail product-detail--missing">
        <p className="eyebrow">404</p>
        <h1>Product not found</h1>
        <p>The piece you are looking for is no longer in this edit.</p>
        <Link className="text-link" to="/shop">
          Return to new arrivals
        </Link>
      </main>
    );
  }

  const handleAddToCart = () => {
    if (!selectedSize) {
      setOptionError("Please select your size before adding this piece.");
      return;
    }

    addToCart(product.id, quantity, selectedSize, selectedColor);
    setOptionError("");
    setAdded(true);
    if (typeof openCart === "function") openCart();
    window.setTimeout(() => setAdded(false), 2400);
  };

  const unavailableSizes = Array.isArray(product.unavailableSizes)
    ? product.unavailableSizes.map((size) => String(size).toUpperCase())
    : [];

  const scrollToImage = (nextIndex) => {
    if (!galleryImages.length) return;
    const boundedIndex = Math.min(
      galleryImages.length - 1,
      Math.max(0, nextIndex)
    );
    setSelectedImage(boundedIndex);

    const track = mobileGalleryRef.current;
    const slide = track?.children?.[boundedIndex];
    if (track && slide) {
      track.scrollTo({ left: slide.offsetLeft, behavior: "smooth" });
    }
  };

  const handleGalleryScroll = (event) => {
    const track = event.currentTarget;
    if (!track.clientWidth) return;
    const nextIndex = Math.min(
      galleryImages.length - 1,
      Math.max(0, Math.round(track.scrollLeft / track.clientWidth))
    );
    if (nextIndex !== selectedImage) setSelectedImage(nextIndex);
  };

  const closeSizeGuide = () => {
    setSizeGuideOpen(false);
    window.requestAnimationFrame(() => sizeGuideTriggerRef.current?.focus());
  };

  const wished = isWishlisted(product.id);

  return (
    <main className="product-detail">
      <nav className="product-breadcrumb" aria-label="Breadcrumb">
        <Link to="/">Home</Link>
        <span>/</span>
        <Link to="/shop">New arrivals</Link>
        <span>/</span>
        <span aria-current="page">{product.name}</span>
      </nav>

      <section className="product-detail__container">
        <div className="gallery-column">
          {galleryImages.length > 1 && (
            <div className="thumbs" aria-label="Product images">
              {galleryImages.map((image, index) => (
                <button
                  key={image}
                  type="button"
                  className={`thumb ${index === selectedImage ? "active" : ""}`}
                  onClick={() => setSelectedImage(index)}
                  aria-label={`Show ${product.name} view ${index + 1}`}
                  aria-pressed={index === selectedImage}
                >
                  <ProductImage src={image} alt="" />
                </button>
              ))}
            </div>
          )}

          <div className="main-image">
            <ProductImage
              src={galleryImages[selectedImage]}
              alt={`${product.name} in ${selectedColor}`}
            />
            <span className="image-counter">
              {selectedImage + 1} / {galleryImages.length}
            </span>
          </div>

          <div
            className="mobile-gallery"
            role="region"
            aria-label={`${product.name} image gallery`}
          >
            <div
              ref={mobileGalleryRef}
              className="mobile-gallery__track"
              onScroll={handleGalleryScroll}
              onKeyDown={(event) => {
                if (event.key === "ArrowLeft") {
                  event.preventDefault();
                  scrollToImage(selectedImage - 1);
                }
                if (event.key === "ArrowRight") {
                  event.preventDefault();
                  scrollToImage(selectedImage + 1);
                }
              }}
              tabIndex={galleryImages.length > 1 ? 0 : -1}
            >
              {galleryImages.map((image, index) => (
                <div
                  className="mobile-gallery__slide"
                  aria-label={`Image ${index + 1} of ${galleryImages.length}`}
                  key={`${image}-${index}`}
                >
                  <ProductImage
                    src={image}
                    alt={`${product.name} view ${index + 1}`}
                  />
                </div>
              ))}
            </div>
            <span className="mobile-gallery__counter" aria-live="polite">
              {selectedImage + 1} / {galleryImages.length}
            </span>
            {galleryImages.length > 1 && (
              <div className="mobile-gallery__controls">
                <button
                  type="button"
                  onClick={() => scrollToImage(selectedImage - 1)}
                  disabled={selectedImage === 0}
                  aria-label="Previous product image"
                >
                  <FiChevronLeft aria-hidden="true" />
                </button>
                <button
                  type="button"
                  onClick={() => scrollToImage(selectedImage + 1)}
                  disabled={selectedImage === galleryImages.length - 1}
                  aria-label="Next product image"
                >
                  <FiChevronRight aria-hidden="true" />
                </button>
              </div>
            )}
          </div>
        </div>

        <aside className="product-info">
          <div className="product-badges">
            {product.isNew && <span>New</span>}
            {product.attributes?.sustainability && (
              <span>{product.attributes.sustainability}</span>
            )}
          </div>

          <div className="product-meta">
            <h1 className="product-name">{product.name}</h1>
            <p className="product-price">{formatPrice(product.price)}</p>
            {product.stock <= 10 && product.stock > 0 && (
              <p className="stock-note">Only {product.stock} left in stock</p>
            )}
          </div>

          <div className="product-options">
            <fieldset className="option-group">
              <legend>
                Color <span>{selectedColor}</span>
              </legend>
              <div className="color-options">
                {product.colors.map((color) => (
                  <button
                    key={color}
                    type="button"
                    className={`color-swatch ${selectedColor === color ? "selected" : ""}`}
                    onClick={() => setSelectedColor(color)}
                    title={color}
                    aria-label={`Select ${color}`}
                    aria-pressed={selectedColor === color}
                  >
                    <span
                      style={{ backgroundColor: colorValues[color] || "#c2c2c2" }}
                    />
                    {selectedColor === color && <FiCheck />}
                  </button>
                ))}
              </div>
            </fieldset>

            <fieldset className="option-group">
              <legend>
                Size{" "}
                <button
                  ref={sizeGuideTriggerRef}
                  className="size-guide-trigger"
                  type="button"
                  aria-haspopup="dialog"
                  aria-expanded={sizeGuideOpen}
                  aria-controls="product-size-guide"
                  onClick={() => setSizeGuideOpen(true)}
                >
                  Size guide
                </button>
              </legend>
              <div className="size-options">
                {product.sizes.map((size) => {
                  const unavailable = unavailableSizes.includes(
                    String(size).toUpperCase()
                  );

                  return (
                    <button
                      key={size}
                      type="button"
                      className={`${selectedSize === size ? "selected" : ""}${
                        unavailable ? " unavailable" : ""
                      }`}
                      onClick={() => {
                        setSelectedSize(size);
                        setOptionError("");
                      }}
                      disabled={unavailable}
                      aria-label={unavailable ? `${size}, sold out` : `Select size ${size}`}
                      aria-pressed={selectedSize === size}
                      title={unavailable ? "Sold out" : undefined}
                    >
                      <span>{size}</span>
                      {unavailable && <span className="sr-only">Sold out</span>}
                    </button>
                  );
                })}
              </div>
            </fieldset>

            {optionError && (
              <p className="option-error" role="alert">
                {optionError}
              </p>
            )}
          </div>

          <div className="product-actions">
            <div className="quantity-control" aria-label="Quantity">
              <button
                type="button"
                onClick={() => setQuantity((current) => Math.max(1, current - 1))}
                aria-label="Decrease quantity"
              >
                <FiMinus />
              </button>
              <span aria-live="polite">{quantity}</span>
              <button
                type="button"
                onClick={() => setQuantity((current) => current + 1)}
                aria-label="Increase quantity"
              >
                <FiPlus />
              </button>
            </div>
            <button type="button" className="add-to-cart" onClick={handleAddToCart}>
              {added ? "Added to bag" : selectedSize ? "Add to bag" : "Select your size"}
            </button>
            <button
              type="button"
              className={`wish ${wished ? "active" : ""}`}
              onClick={() => toggleWishlist(product.id)}
              aria-label={wished ? "Remove from wishlist" : "Add to wishlist"}
              aria-pressed={wished}
            >
              <FiHeart />
            </button>
          </div>

          <ul className="product-usps">
            <li>Members earn points with every online purchase</li>
            <li>Orders placed before 8 PM ship the same working day</li>
            <li>Easy returns within {siteConfig.shipping.returnDays} days</li>
          </ul>

          <div className="product-accordions">
            <details open>
              <summary>Description</summary>
              <div>
                {product.description.map((paragraph) => (
                  <p key={paragraph}>{paragraph}</p>
                ))}
              </div>
            </details>
            <details>
              <summary>Product details</summary>
              <ul>
                {product.features.map((feature) => (
                  <li key={feature}>{feature}</li>
                ))}
                <li>Material: {product.attributes.material}</li>
                <li>Care: {product.attributes.care}</li>
                <li>Article: {product.sku}</li>
              </ul>
            </details>
            <details>
              <summary>Size &amp; fit</summary>
              <p>{product.attributes.fit}. Designed to feel easy and true to size.</p>
            </details>
          </div>

          <div className="conscious-note">
            <p className="eyebrow">Made with care</p>
            <p>
              We select durable, lower-impact materials and design every piece to
              move naturally between seasons.
            </p>
          </div>
        </aside>
      </section>

      <section className="related-products" aria-labelledby="related-title">
        <h2 id="related-title">You may also like</h2>
        <div className="related-products__grid">
          {relatedProducts.map((related) => (
            <Link key={related.id} to={`/shopDetails/${related.id}`}>
              <ProductImage src={related.image} alt={related.name} />
              <div>
                <h3>{related.name}</h3>
                <span>{formatPrice(related.price)}</span>
              </div>
            </Link>
          ))}
        </div>
      </section>

      <div className="mobile-buy-bar">
        <button type="button" onClick={handleAddToCart}>
          {added
            ? "Added to bag"
            : selectedSize
              ? `Add to bag · ${formatPrice(product.price)}`
              : "Select your size"}
        </button>
      </div>

      {sizeGuideOpen && (
        <div className="size-guide-layer">
          <button
            className="size-guide-backdrop"
            type="button"
            tabIndex={-1}
            aria-label="Close size guide"
            onClick={closeSizeGuide}
          />
          <section
            id="product-size-guide"
            className="size-guide-dialog"
            role="dialog"
            aria-modal="true"
            aria-labelledby="size-guide-title"
            aria-describedby="size-guide-description"
          >
            <header>
              <div>
                <p className="eyebrow">Measurements</p>
                <h2 id="size-guide-title">Size guide</h2>
              </div>
              <button
                ref={sizeGuideCloseRef}
                type="button"
                aria-label="Close size guide"
                onClick={closeSizeGuide}
              >
                <FiX aria-hidden="true" />
              </button>
            </header>
            <p id="size-guide-description">
              Use your body measurements to choose the most comfortable size.
              Measurements are shown in centimetres.
            </p>
            <div className="size-guide-table-wrap">
              <table>
                <caption className="sr-only">Body measurements in centimetres</caption>
                <thead>
                  <tr>
                    <th scope="col">Size</th>
                    <th scope="col">Bust</th>
                    <th scope="col">Waist</th>
                    <th scope="col">Hip</th>
                  </tr>
                </thead>
                <tbody>
                  {SIZE_GUIDE.map((row) => (
                    <tr key={row.size}>
                      <th scope="row">{row.size}</th>
                      <td>{row.bust}</td>
                      <td>{row.waist}</td>
                      <td>{row.hip}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="size-guide-dialog__note">
              Between sizes? Choose the larger size for a more relaxed fit.
            </p>
          </section>
        </div>
      )}
    </main>
  );
};

export default ProductDetail;
